import { useEffect, useState } from "react";

export function useLocalStorageState<T>(key: string, initialValue: T) {
  const parseValue = (value: string | null) => {
    try {
      return value ? JSON.parse(value) : initialValue;
    } catch {
      return initialValue;
    }
  };

  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return initialValue;
    return parseValue(localStorage.getItem(key));
  });

  const updateValue = (newValue: T) => {
    if (typeof window === "undefined") return;

    localStorage.setItem(key, JSON.stringify(newValue));
    setValue(newValue);
    window.dispatchEvent(new Event("local-storage-change"));
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleStorageChange = () => {
      setValue(parseValue(localStorage.getItem(key)));
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("local-storage-change", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("local-storage-change", handleStorageChange);
    };
  }, [key, initialValue]);

  return [value, updateValue] as const;
}